import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Box,
} from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { userApi } from '@/api/user';
import { useAuth } from '@/hooks/useAuth';

export const GlobalMessageResyncButton: React.FC = () => {
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [progress, setProgress] = useState<{ queued: number; total: number } | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const mutation = useMutation({
    mutationFn: async () => {
      const after = new Date(`${fromDate}Z`);
      const before = new Date(`${toDate}Z`);
      if (
        !fromDate ||
        !toDate ||
        Number.isNaN(after.getTime()) ||
        Number.isNaN(before.getTime()) ||
        after >= before
      ) {
        throw new Error('Choose a valid date range before starting the resync.');
      }

      // Do not trust cached connection status here. The resync API refreshes
      // each mapped account directly from Unipile before it queues the job.
      const mappedUsers = (await teamApi.getAllUsers()).filter((candidate) =>
        Boolean(candidate.unipile_id)
      );
      if (mappedUsers.length === 0) {
        throw new Error('No LinkedIn accounts were found.');
      }

      setProgress({ queued: 0, total: mappedUsers.length });
      const failedUsers: string[] = [];
      let queued = 0;
      for (const mappedUser of mappedUsers) {
        try {
          await userApi.createSyncMessagesJob(
            mappedUser.key,
            after.toISOString(),
            before.toISOString()
          );
          queued += 1;
          setProgress({ queued, total: mappedUsers.length });
        } catch {
          failedUsers.push(mappedUser.email || mappedUser.name || mappedUser.key);
        }
      }

      if (failedUsers.length > 0) {
        throw new Error(
          `${queued} of ${mappedUsers.length} resync jobs were queued. Failed: ${failedUsers.join(', ')}`
        );
      }
      return queued;
    },
    onSuccess: (queued) => {
      setIsDialogOpen(false);
      setToast({
        open: true,
        message: `Queued message resync for ${queued} connected LinkedIn accounts.`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setToast({
        open: true,
        message: error.message || 'Failed to queue the global message resync.',
        severity: 'error',
      });
    },
  });

  if (!user?.is_staff && !user?.is_superuser) return null;

  const openDialog = () => {
    const now = new Date();
    const ninetyDaysAgo = new Date(now);
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    setFromDate(ninetyDaysAgo.toISOString().slice(0, 16));
    setToDate(now.toISOString().slice(0, 16));
    setProgress(null);
    setIsDialogOpen(true);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SyncIcon />}
        onClick={openDialog}
        disabled={mutation.isPending}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Resync all
      </Button>

      <Dialog
        open={isDialogOpen}
        onClose={() => {
          if (!mutation.isPending) setIsDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resync all LinkedIn messages</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            This queues a full message resync for every connected LinkedIn account.
            Existing messages are deduplicated by their provider message ID. All dates and
            times below use Coordinated Universal Time (UTC), regardless of your browser timezone.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="From (UTC)"
              type="datetime-local"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={mutation.isPending}
            />
            <TextField
              label="To (UTC)"
              type="datetime-local"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={mutation.isPending}
            />
          </Box>
          {progress && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Queued {progress.queued} of {progress.total} accounts…
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={mutation.isPending ? <CircularProgress size={18} /> : <SyncIcon />}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Queuing resyncs…' : 'Start full resync'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={8000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((current) => ({ ...current, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};
